import { create } from "zustand";

export type CartSource = "local" | "server" | "none";

interface CartSnapshot {
  shopId: string | null;
  source: CartSource;
  quantities: Record<string, number>;
}

interface CartStoreState extends CartSnapshot {
  totalQuantity: number;
  setSnapshot: (snapshot: CartSnapshot) => void;
}

/**
 * Global read-model for the customer's cart, kept in sync by
 * `useCustomerCartSync` (mounted once in AppLayout) from whichever cart is
 * authoritative — localStorage for guests, the server cart for signed-in
 * customers. Components read from this store instead of each re-deriving
 * "current quantity per product" from local storage + React Query.
 */
export const useCartStore = create<CartStoreState>((set) => ({
  shopId: null,
  source: "none",
  quantities: {},
  totalQuantity: 0,
  setSnapshot: ({ shopId, source, quantities }) =>
    set({
      shopId,
      source,
      quantities,
      totalQuantity: Object.values(quantities).reduce(
        (sum, quantity) => sum + quantity,
        0,
      ),
    }),
}));

export function useCartQuantity(productId: string | undefined): number {
  return useCartStore((state) =>
    productId ? (state.quantities[productId] ?? 0) : 0,
  );
}

export function useCartTotalQuantity(): number {
  return useCartStore((state) => state.totalQuantity);
}

export function useCartSource(): CartSource {
  return useCartStore((state) => state.source);
}
